import React, { useState, useEffect, useContext, useCallback } from "react";
import createAuth0Client, {
  Auth0Client,
  Auth0ClientOptions,
  RedirectLoginOptions,
} from "@auth0/auth0-spa-js";
import { usePostHog } from "@posthog/react"
import UserApi, { GetDBUserOptions } from "Api/UserApi";
import { User } from "../types/Auth";
import { PlanData } from "../types/Plan";
import { useAppContext } from "Context";

type RefreshUserFunction = (options?: GetDBUserOptions) => Promise<void>;

export type LoginWithRedirectOptions<TAppState = any> =
  RedirectLoginOptions<TAppState> & {
    /**
     * Route to direct to after login. Path must be whitelisted.
     * If provided, overwrites {@link RedirectLoginOptions.redirect_uri}.
     */
    redirect_to?: string;
  };

type LoginWithRedirect<TAppState = any> = (
  options?: LoginWithRedirectOptions<TAppState>
) => Promise<void>;

type AuthState = Pick<
  Auth0Client,
  "getIdTokenClaims" | "getTokenWithPopup" | "loginWithPopup" | "logout"
> & {
  isAuthenticated: boolean;
  user: User;
  loading: boolean;
  popupOpen: boolean;
  refreshUser: RefreshUserFunction;
  refreshUserSilently: RefreshUserFunction;
  handleRedirectCallback: () => Promise<void>;
  loginAndReturn: LoginWithRedirect;
  loginWithRedirect: LoginWithRedirect;
  /**
   * Open the Auth0 login popup for a recognized donor, pre-filling their email.
   * The popup establishes the real session; resolves with the refreshed user
   * (including plan data) so the caller can route by membership.
   */
  loginWithEmail: (email: string) => Promise<User>;
  /**
   * Sign in with credentials already held on the page — no popup and no hosted
   * login UI, so the donor is never asked for a password they just chose. Used
   * right after post-donation account creation.
   *
   * Redeeming the credentials navigates to Auth0 and back, so this does NOT
   * resolve on success — the page unloads. Persist anything needed on the return
   * before calling it. It only rejects, when the credentials or the tenant's
   * cross-origin settings won't allow the sign-in.
   */
  loginWithPassword: (email: string, password: string) => Promise<void>;
  /**
   * Merge known plan fields into the in-memory user immediately (no re-query).
   * Used right after a donation to refresh the header's active/inactive state
   * from the checkout response, avoiding the Chargebee-list sync lag a re-read
   * would hit. See {@link refreshUser} for the (syncing) re-read variant.
   */
  applyPlanData: (planData: Partial<User>) => void;
};

export const Auth0Context = React.createContext<AuthState>({} as AuthState);
export const useAuth0 = () => useContext(Auth0Context);
export let lastUser: any = undefined;
export let auth0Initialized = false;

let _initOptions: Auth0ClientOptions;

const getAuth0Client = (_?: Auth0ClientOptions): Promise<Auth0Client> => {
  return new Promise(async (resolve, reject) => {
    let client;
    if (!client) {
      try {
        client = await createAuth0Client(_initOptions);
        resolve(client);
      } catch (e) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        reject(new Error("getAuth0Client Error", e));
      }
    }
  });
};

export const getPKCEState = async (): Promise<any> => {
  const client = await getAuth0Client();
  const authorizeUrl = await client.buildAuthorizeUrl({
    redirect_uri: window.location.origin,
    response_type: "code",
  });

  // parse out the PKCE + state bits
  const responseObject: any = {};
  const params = new URL(authorizeUrl).searchParams;
  responseObject.state = params.get("state");
  responseObject.code_challenge = params.get("code_challenge");
  responseObject.code_challenge_method = params.get("code_challenge_method");
  responseObject.nonce = params.get("nonce");

  return responseObject;
};

export const getTokenSilently = async (
  ...p: Parameters<Auth0Client["getTokenSilently"]>
): ReturnType<Auth0Client["getTokenSilently"]> => {
  const client = await getAuth0Client();
  return await client.getTokenSilently(...p);
};

export const getUser = async (
  ...p: Parameters<Auth0Client["getUser"]>
): Promise<User> => {
  const client = await getAuth0Client();
  const user = await client.getUser(...p);
  const planData = await getPlan(user?.email as string, { sync: false });
  const jgUser = { ...user, ...planData };
  lastUser = jgUser;
  return jgUser;
};

async function getPlan(
  email: string,
  options?: GetDBUserOptions
): Promise<PlanData> {
  try {
    const response = await UserApi.getDBUser(email, options);
    return {
      plan: response.planId,
      subscriptionId: response.subscriptionId,
      ttl: response.subscriptionCurrentTermEnd,
      free_trial: response.free_trial,
      subscriptionStatus: response.subscription_status,
    };
  } catch (e) {
    return {
      plan: "",
      subscriptionId: "",
    };
  }
}

export const Auth0Provider = ({
  children,
  onRedirectCallback,
  ...initOptions
}: any) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User>({} as User);
  const [auth0Client, setAuth0] = useState<Auth0Client>();
  const [loading, setLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);
  const { activeChannel, setActiveChannel } = useAppContext();

  const posthog = usePostHog();

  useEffect(() => {
    const initAuth0 = async () => {
      _initOptions = initOptions;
      const client = await getAuth0Client(initOptions);
      setAuth0(client);
      try {
        let justLoggedIn = false;
        if (window.location.search.includes("code=")) {
          const { appState } = await client.handleRedirectCallback();

          // Forcing a new token to prevent issues with Lock. Best-effort only:
          // handleRedirectCallback has already cached real tokens, while this call
          // uses the silent hidden-iframe flow, which fails whenever third-party
          // cookies are blocked or the tenant interrupts /authorize. Letting it
          // throw here skipped the session publication below, so a completed login
          // came back looking signed out.
          try {
            await client.getTokenSilently();
          } catch (tokenErr) {
            console.warn("Post-callback token refresh skipped:", tokenErr);
          }
          onRedirectCallback(appState);
          justLoggedIn = true;
        }
        const isAuthenticated = await client.isAuthenticated();

        if (isAuthenticated) {
          const user = await client.getUser();
          // On a fresh login (redirect callback), force a synced plan read
          // (Chargebee → Dynamo) so the header reflects the real membership.
          // Regular page loads use the cached read to avoid syncing every load.
          const planData = await getPlan(
            user?.email as string,
            justLoggedIn ? { sync: true } : undefined
          );
          lastUser = { ...user, ...planData };
          setUser({ ...user, ...planData });
          localStorage.setItem("hasAuthenticated", "true");

          posthog?.identify(user?.user_id,{
            user_id: user?.email,
            name: user?.name,
            plan_type: planData.plan,
            is_authenticated: true,
          });

          setIsAuthenticated(isAuthenticated);
        } else {
          posthog?.identify(user?.user_id,{
            plan_type: "free",
            is_authenticated: false,
          });
        }
      } catch (err) {
        console.error("Error occured: ", err);
      }

      auth0Initialized = true;

      setLoading(false);
    };
    initAuth0();
    // eslint-disable-next-line
  }, []);

  /**
   * Refresh the user. Note that unlike {@link UserApi.getDBUser}, this defaults
   * to {@link GetDBUserOptions.sync} being true to force the plan to refresh, too.
   */
  const refreshUser: RefreshUserFunction = useCallback(
    async (options: GetDBUserOptions = { sync: true }) => {
      await auth0Client?.getTokenSilently({ ignoreCache: true });
      const user = await auth0Client?.getUser();
      const planData = await getPlan(user?.email as string, options);
      setUser({ ...user, ...planData });
      lastUser = { ...user, ...planData };

      const isAuthenticated = await auth0Client?.isAuthenticated();

      if (isAuthenticated) {

        posthog?.identify(user?.user_id,{
          user_id: user?.email,
          name: user?.name,
          plan_type: planData.plan,
          is_authenticated: true,
        });

        setIsAuthenticated(isAuthenticated);
      } else {
        posthog?.identify(user?.user_id,{
          plan_type: "free",
          is_authenticated: false,
        });
      }

      // adding this to reset the player after a user signs in.
      // Optional call: the app context defaults to an empty object, so this is
      // undefined whenever the auth provider renders outside App's context
      // provider — and throwing here would abort a sign-in that already succeeded.
      setActiveChannel?.(activeChannel);

      
    },
    [activeChannel, auth0Client, setActiveChannel, posthog]
  );

  /**
   * Refresh the user while suppressing any errors. Note that unlike {@link UserApi.getDBUser}, this defaults
   * to {@link GetDBUserOptions.sync} being true to force the plan to refresh, too.
   */
  /**
   * Immediately merge plan fields into the in-memory user without re-querying.
   * Lets a just-completed donation update the header from the checkout response,
   * so it doesn't briefly show the stale (pre-donation) membership state.
   */
  const applyPlanData = useCallback((planData: Partial<User>) => {
    setUser((prev) => {
      const merged = { ...prev, ...planData } as User;
      lastUser = merged;
      return merged;
    });
  }, []);

  const refreshUserSilently: RefreshUserFunction = useCallback(
    async (options: GetDBUserOptions = { sync: true }) => {
      try {
        await refreshUser(options);
      } catch (_) {}
    },
    [refreshUser]
  );

  const getUserAndSetAuth = async () => {
    await refreshUser({ sync: true });
    setIsAuthenticated(true);
  };

  const loginWithPopup = async (params = {}) => {
    setPopupOpen(true);
    try {
      await auth0Client?.loginWithPopup(params);
    } catch (error) {
      console.error(error);
    } finally {
      setPopupOpen(false);
    }

    await getUserAndSetAuth();
  };

  const loginWithEmail = async (email: string): Promise<User> => {
    // Recognized donor: open the Auth0 popup with the email pre-filled as a
    // login hint. (Locking the email / showing password-only is an Auth0
    // Universal Login "Identifier First" tenant setting, not a client option.)
    //
    // loginWithPopup already returns and caches fresh tokens, so we hydrate our
    // auth state straight from that cache. We deliberately do NOT route through
    // refreshUser here: it forces getTokenSilently({ ignoreCache: true }), whose
    // hidden-iframe silent re-auth fails right after a popup unless third-party
    // cookies / "Allowed Web Origins" are configured — which is exactly what
    // made a correct password error out with "We couldn't sign you in."
    // `prompt: "login"` forces Auth0 to re-authenticate instead of silently
    // reusing an existing SSO session. Without it, once any user has signed in,
    // the popup reuses that session (jumping to the consent/Accept screen) and
    // ignores both `login_hint` and the email the visitor just entered — so a
    // different recognized email would sign in as the previous user. Forcing
    // login makes the entered email authoritative.
    await auth0Client?.loginWithPopup(
      email ? { login_hint: email, prompt: "login" } : { prompt: "login" }
    );
    if (!(await auth0Client?.isAuthenticated())) {
      throw new Error("Auth0 popup closed before authentication completed");
    }
    const auth0User = await auth0Client?.getUser();
    // Enforce that the signed-in account matches the email entered on the page.
    // The popup only pre-fills the hint — a user can authenticate as a different
    // account. If they do, clear the session (localOnly → no redirect, page stays
    // put) and reject, so we never sign them in as an account they didn't enter.
    // The lingering Auth0 session is harmless: the next attempt uses prompt:"login"
    // and re-authenticates, so it's never silently reused.
    const enteredEmail = (email || "").trim().toLowerCase();
    const signedInEmail = (auth0User?.email || "").trim().toLowerCase();
    if (enteredEmail && signedInEmail && enteredEmail !== signedInEmail) {
      await auth0Client?.logout({ localOnly: true });
      setIsAuthenticated(false);
      setUser({} as User);
      localStorage.removeItem("hasAuthenticated");
      throw new Error(
        `You signed in as ${auth0User?.email}, but the email entered was ${email}. Please sign in with ${email}.`
      );
    }
    return await hydrateSession(auth0User);
  };

  /**
   * Load the signed-in user's plan and publish the authenticated session to the
   * app. Shared by every login path that establishes the session itself (popup,
   * cross-origin) rather than going through the redirect callback.
   */
  const hydrateSession = async (auth0User: any): Promise<User> => {
    const planData = await getPlan(auth0User?.email as string, { sync: true });
    const jgUser = { ...auth0User, ...planData } as User;
    lastUser = jgUser;
    setUser(jgUser);
    setIsAuthenticated(true);
    localStorage.setItem("hasAuthenticated", "true");
    posthog?.identify(auth0User?.user_id);
    setActiveChannel?.(activeChannel);
    return jgUser;
  };

  /**
   * Auth0 cross-origin authentication: sign the donor in with credentials the page
   * already holds, so they are never asked for the password a second time and no
   * hosted-login UI or popup appears. Two steps:
   *
   *  1. POST /co/authenticate exchanges the email + password for a single-use
   *     `login_ticket`. A ticket is not a session — it has to be redeemed.
   *  2. The ticket is redeemed by a normal redirect to /authorize. Auth0 completes
   *     the login and returns to the app, where the provider's existing
   *     `code=`-in-the-URL handler exchanges it and publishes the session.
   *
   * This navigates: the call does not resolve, the page unloads. The caller must
   * therefore persist anything it needs on the way back.
   *
   * A hidden iframe was tried instead and cannot work here: this tenant's login
   * pipeline answers /authorize with `302 /authorize/resume`, an interruption that
   * has to render a page, and Auth0 forbids framing it. Nor can the SDK's
   * getTokenSilently() redeem a ticket — it hardcodes `prompt=none`, which tells
   * Auth0 to reuse an existing session and never authenticate anyone.
   */
  const loginWithPassword = async (
    email: string,
    password: string
  ): Promise<void> => {
    const response = await fetch(
      `https://${process.env.REACT_APP_AUTH0_DOMAIN}/co/authenticate`,
      {
        method: "POST",
        // Auth0 sets the cross-origin auth cookie on this call.
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_id: process.env.REACT_APP_AUTH0_CLIENT_ID,
          credential_type:
            "http://auth0.com/oauth/grant-type/password-realm",
          username: email,
          password,
          realm: "Username-Password-Authentication",
        }),
      }
    );
    if (!response.ok) {
      throw new Error(
        `Auth0 cross-origin authentication failed (${response.status})`
      );
    }
    const { login_ticket: loginTicket } = await response.json();
    if (!loginTicket) {
      throw new Error("Auth0 returned no login ticket");
    }

    // Redeem the ticket. `appState.targetUrl` is where index.tsx's
    // onRedirectCallback sends the app once the code has been exchanged.
    await auth0Client?.loginWithRedirect({
      login_ticket: loginTicket,
      realm: "Username-Password-Authentication",
      appState: { targetUrl: "/" },
    } as any);
  };

  const handleRedirectCallback = async () => {
    setLoading(true);
    await auth0Client?.handleRedirectCallback();
    await getUserAndSetAuth();
    setLoading(false);
  };

  const loginWithRedirect: LoginWithRedirect = async <TAppState extends any>(
    options?: LoginWithRedirectOptions<TAppState>
  ): Promise<void> => {
    if (options) {
      const { redirect_to, ...redirectOptions } = options;

      if (redirect_to?.startsWith("/")) {
        redirectOptions.redirect_uri = `${process.env.REACT_APP_DOMAIN}${redirect_to}`;
      }

      options = redirectOptions;
    }

    return auth0Client?.loginWithRedirect(options);
  };

  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        popupOpen,
        refreshUser,
        refreshUserSilently,
        applyPlanData,
        loginWithPopup,
        handleRedirectCallback,
        getIdTokenClaims: (...p: any[]) =>
          auth0Client?.getIdTokenClaims(...p) as ReturnType<
            Auth0Client["getIdTokenClaims"]
          >,
        loginWithRedirect,
        loginWithEmail,
        loginWithPassword,
        getTokenWithPopup: (...p: any[]) =>
          auth0Client?.getTokenWithPopup(...p) as ReturnType<
            Auth0Client["getTokenWithPopup"]
          >,
        logout: (...p: any[]) => {
          // Clear our app-set auth latch. The SDK clears its own cache on
          // logout but not this flag, which would otherwise keep the header's
          // "you're no longer signed in" re-login prompt latched on.
          localStorage.removeItem("hasAuthenticated");
          const opts = p[0] || {};
          // Full (redirecting) logout: return to a public, non-guarded page.
          // Without an explicit returnTo, Auth0 sends the user to the first
          // "Allowed Logout URL", which can be a route that auto-triggers login
          // (/account, /login) → interactive /authorize → consent screen. A
          // localOnly logout skips the /v2/logout redirect entirely.
          if (!opts.localOnly && !opts.returnTo) {
            opts.returnTo = window.location.origin;
          }
          const result = auth0Client?.logout(opts);
          // Only a localOnly logout resets the React auth state here: it stays on
          // the page, so nothing else would clear it (the SDK only clears its own
          // token cache).
          //
          // A redirecting logout must NOT. logout() merely schedules the
          // navigation to /v2/logout, so flipping isAuthenticated re-renders
          // first — and on a guarded route PrivateRoute's effect immediately
          // fires loginWithRedirect, whose window.location.assign overrides the
          // pending logout. Signing out from /account then landed the user on the
          // Auth0 login/consent screen, still signed in.
          if (opts.localOnly) {
            setIsAuthenticated(false);
            setUser({} as User);
          }
          return result;
        },
        loginAndReturn: (opts) => {
          opts = opts || {};
          opts.appState = opts.appState || {};
          opts.appState.targetUrl = window.location.pathname;
          return loginWithRedirect(opts);
        },
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};
